/**
 * AccountModal.js — in-app account management (replaces the /account EJS page).
 *
 * Part of the "Express becomes a pure API server" plan: everything the account
 * page did is reachable here through the JSON API (authAPI.changeName /
 * changePassword, stripeAPI.portal / checkout). Playback logic is untouched —
 * this is a self-contained EDIT surface, opened from the FILE menu.
 *
 * Props:
 * - account:            { name, email, tier } — the signed-in account.
 * - isPremium:          boolean.
 * - onClose:            () => void.
 * - onLogout:           () => void — sign out (closes the modal too).
 * - onUpgrade:          () => void — Stripe checkout for a plan.
 * - onManage:           () => void — Stripe billing portal.
 * - onChangeName:       (name) => Promise — persists + updates app state.
 * - onChangePassword:   (current, next) => Promise — token is re-issued by the API layer.
 * - onRequestDeletion:  (password, confirmation) => Promise<{dueAt}> — 30-day grace flow.
 */

import { useState } from 'react';
import { useI18n } from './i18n';

const inputStyle = {
  width: '100%', boxSizing: 'border-box', padding: '8px 10px',
  background: '#0f172a', border: '1px solid #475569', borderRadius: 6,
  color: '#e2e8f0', fontSize: 13, outline: 'none',
};
const labelStyle = { fontSize: 11, color: '#94a3b8', margin: '10px 0 4px', display: 'block' };
const btn = (bg, fg = '#fff') => ({
  padding: '8px 14px', border: 'none', borderRadius: 6, cursor: 'pointer',
  background: bg, color: fg, fontSize: 12, fontWeight: 700,
});
const sectionStyle = { borderTop: '1px solid #334155', marginTop: 14, paddingTop: 12 };

export default function AccountModal({
  account, isPremium, monthlyPrice = null, onClose, onLogout, onUpgrade, onManage,
  onChangeName, onChangePassword, onRequestDeletion = null,
}) {
  const { t, price: money } = useI18n();
  const price = money(monthlyPrice);
  const [name, setName] = useState(account?.name || '');
  const [pw, setPw] = useState({ current: '', next: '', confirm: '' });
  const [del, setDel] = useState({ open: false, pw: '', conf: '' });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null); // { kind: 'ok' | 'err', text }

  const run = async (fn, okText) => {
    setBusy(true); setMsg(null);
    try {
      await fn();
      if (okText) setMsg({ kind: 'ok', text: okText });
    } catch (e) {
      setMsg({ kind: 'err', text: e.message || String(e) });
    } finally {
      setBusy(false);
    }
  };

  const saveName = () => {
    const trimmed = name.trim();
    if (!trimmed || trimmed === account?.name) return;
    run(() => onChangeName(trimmed), t('account.nameDone'));
  };

  const savePw = () => {
    if (pw.next.length < 6) { setMsg({ kind: 'err', text: t('account.shortPw') }); return; }
    if (pw.next !== pw.confirm) { setMsg({ kind: 'err', text: t('account.pwMismatch') }); return; }
    run(async () => {
      await onChangePassword(pw.current, pw.next);
      setPw({ current: '', next: '', confirm: '' });
    }, t('account.pwDone'));
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9998, background: 'rgba(0,0,0,0.7)',
      display: 'flex', justifyContent: 'center', alignItems: 'center',
    }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{
        background: '#1e293b', borderRadius: 12, padding: '1.25rem 1.5rem',
        width: '100%', maxWidth: 420, color: '#e2e8f0',
        maxHeight: 'calc(var(--kv-viewport-h) * 0.85)', overflowY: 'auto',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ margin: 0, fontSize: '1.15rem' }}>{t('account.title')}</h2>
          <button onClick={onClose} style={{
            background: '#475569', border: 'none', borderRadius: 6, color: '#fff',
            width: 30, height: 30, cursor: 'pointer', fontSize: '0.9rem',
          }}>✕</button>
        </div>

        {/* Identity line: name · email · plan badge */}
        <div style={{ marginTop: 10, fontSize: 13, color: '#94a3b8' }}>
          <span style={{ color: '#e2e8f0', fontWeight: 700 }}>{account?.name}</span>
          {account?.email ? ` · ${account.email}` : ''}
          <span style={{
            marginLeft: 8, padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 700,
            background: isPremium ? '#7c3aed' : '#334155', color: '#fff',
          }}>{isPremium ? t('account.premiumOn') : t('account.freePlan')}</span>
        </div>

        {msg && (
          <div style={{
            marginTop: 10, padding: '7px 10px', borderRadius: 6, fontSize: 12,
            background: msg.kind === 'ok' ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.15)',
            color: msg.kind === 'ok' ? '#4ade80' : '#f87171',
          }}>{msg.text}</div>
        )}

        {/* ── Display name ── */}
        <div style={sectionStyle}>
          <label style={labelStyle}>{t('account.nameTitle')}</label>
          <div style={{ display: 'flex', gap: 8 }}>
            <input style={inputStyle} value={name} onChange={e => setName(e.target.value)} disabled={busy} />
            <button style={btn('#2563eb')} onClick={saveName} disabled={busy}>{t('account.save')}</button>
          </div>
        </div>

        {/* ── Password ── */}
        <div style={sectionStyle}>
          <label style={labelStyle}>{t('account.passwordTitle')}</label>
          <input style={{ ...inputStyle, marginBottom: 6 }} type="password" placeholder={t('account.currentPw')}
            value={pw.current} onChange={e => setPw(p => ({ ...p, current: e.target.value }))} disabled={busy} />
          <div style={{ display: 'flex', gap: 6 }}>
            <input style={inputStyle} type="password" placeholder={t('account.newPw')}
              value={pw.next} onChange={e => setPw(p => ({ ...p, next: e.target.value }))} disabled={busy} />
            <input style={inputStyle} type="password" placeholder={t('account.confirmPw')}
              value={pw.confirm} onChange={e => setPw(p => ({ ...p, confirm: e.target.value }))} disabled={busy} />
          </div>
          <button style={{ ...btn('#334155'), marginTop: 8 }} onClick={savePw} disabled={busy}>{t('account.changePw')}</button>
        </div>

        {/* ── Subscription ── */}
        <div style={sectionStyle}>
          <label style={labelStyle}>{t('account.subscription')}</label>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {isPremium ? (
              <button style={btn('#334155')} onClick={() => run(onManage)} disabled={busy}>{t('account.manage')}</button>
            ) : (
              <button style={btn('#16a34a')} onClick={() => run(onUpgrade)} disabled={busy}>
                {/* The price is part of the offer, not a surprise at checkout.
                    No number until the backend answers — never "$NaN". */}
                {price ? t('account.upgradePrice', { price }) : t('account.upgrade')}
              </button>
            )}
          </div>
        </div>

        {/* ── Danger zone: request account deletion (30-day grace, server-side) ── */}
        {onRequestDeletion && (
          <div style={sectionStyle}>
            {!del.open ? (
              <button style={{ ...btn('#3f1d1d', '#fca5a5'), width: '100%' }}
                onClick={() => setDel(d => ({ ...d, open: true }))}>{t('account.deleteTitle')}</button>
            ) : (
              <>
                <label style={labelStyle}>{t('account.deleteTitle')}</label>
                <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 6 }}>{t('account.deleteWarn')}</div>
                <input style={{ ...inputStyle, marginBottom: 6 }} type="password" placeholder={t('account.deletePw')}
                  value={del.pw} onChange={e => setDel(d => ({ ...d, pw: e.target.value }))} disabled={busy} />
                <input style={inputStyle} placeholder={t('account.deleteConfirm')}
                  value={del.conf} onChange={e => setDel(d => ({ ...d, conf: e.target.value }))} disabled={busy} />
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <button style={btn('#7f1d1d', '#fecaca')}
                    disabled={busy || !del.pw || del.conf !== 'DELETE'}
                    onClick={() => run(async () => {
                      const res = await onRequestDeletion(del.pw, del.conf);
                      setDel({ open: false, pw: '', conf: '' });
                      setMsg({
                        kind: 'ok',
                        text: `${t('account.deleteDone')} ${res?.dueAt ? new Date(res.dueAt).toLocaleDateString() : ''}`,
                      });
                    })}>{t('account.deleteBtn')}</button>
                  <button style={btn('#334155')} onClick={() => setDel({ open: false, pw: '', conf: '' })} disabled={busy}>
                    {t('account.deleteCancel')}
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {/* ── Sign out ── */}
        <div style={{ ...sectionStyle, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 12, color: '#64748b' }}>{account?.email || ''}</span>
          <button style={btn('#7f1d1d', '#fecaca')} onClick={onLogout}>{t('account.signOut')}</button>
        </div>
      </div>
    </div>
  );
}
